import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ImagePlus, Plus, Trash2, X } from "lucide-react";
import { photoUrl } from "@/components/Portfolio";
import { DEFAULT_SERVICES_PAGE } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full border border-stone-900/15 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors focus:border-[#E75C15]";

export const ServicesPageForm = ({ settings }) => {
    const [blocks, setBlocks] = useState(DEFAULT_SERVICES_PAGE);
    const [saving, setSaving] = useState(false);
    const [uploadingIdx, setUploadingIdx] = useState(null);
    const fileRefs = useRef({});

    useEffect(() => {
        if (Array.isArray(settings?.services_page) && settings.services_page.length) {
            setBlocks(settings.services_page);
        }
    }, [settings]);

    const setBlock = (i, k, v) =>
        setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, [k]: v } : b)));

    const uploadPhoto = async (i, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingIdx(i);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await axios.post(`${API}/upload`, fd, { withCredentials: true });
            setBlock(i, "photo", res.data.url);
            toast.success("Foto caricata.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Caricamento non riuscito.");
        } finally {
            setUploadingIdx(null);
            if (fileRefs.current[i]) fileRefs.current[i].value = "";
        }
    };

    const save = async () => {
        const cleaned = blocks
            .map((b) => ({ title: b.title.trim(), text: b.text.trim(), photo: b.photo || "" }))
            .filter((b) => b.title && b.text);
        if (!cleaned.length) {
            toast.error("Serve almeno un blocco con titolo e testo.");
            return;
        }
        setSaving(true);
        try {
            await axios.put(`${API}/settings/services_page`, { value: cleaned }, { withCredentials: true });
            setBlocks(cleaned);
            toast.success("Pagina servizi aggiornata: è già visibile sul sito.");
        } catch {
            toast.error("Salvataggio non riuscito.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl" data-testid="services-page-form">
            <h2 className="font-display text-2xl font-light text-stone-900">Pagina "I Nostri Servizi"</h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-600">
                La pagina è pubblica all'indirizzo <strong>/servizi</strong>. Ogni blocco ha
                titolo, testo e una foto facoltativa; i blocchi si alternano con foto a destra e
                sinistra.
            </p>

            <div className="mt-8 space-y-6">
                {blocks.map((b, i) => (
                    <div key={i} data-testid={`services-block-${i}`} className="border border-stone-900/10 bg-white p-6">
                        <div className="flex items-center justify-between">
                            <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#C24E0F]">
                                Blocco {String(i + 1).padStart(2, "0")}
                            </span>
                            <button
                                data-testid={`services-block-remove-${i}`}
                                onClick={() => setBlocks((bs) => bs.filter((_, j) => j !== i))}
                                className="p-1.5 text-stone-400 transition-colors hover:text-[#B8430D]"
                                aria-label="Rimuovi blocco"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                    Titolo *
                                </label>
                                <input
                                    data-testid={`services-block-title-${i}`}
                                    className={inputCls}
                                    value={b.title}
                                    onChange={(e) => setBlock(i, "title", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                    Testo *
                                </label>
                                <textarea
                                    data-testid={`services-block-text-${i}`}
                                    rows={4}
                                    className={`${inputCls} resize-y`}
                                    value={b.text}
                                    onChange={(e) => setBlock(i, "text", e.target.value)}
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                {b.photo ? (
                                    <div className="relative h-20 w-28 overflow-hidden border border-stone-900/10">
                                        <img src={photoUrl(b.photo)} alt="" className="h-full w-full object-cover" />
                                        <button
                                            data-testid={`services-block-photo-remove-${i}`}
                                            onClick={() => setBlock(i, "photo", "")}
                                            className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#1C1917]/80 text-stone-100"
                                            aria-label="Rimuovi foto"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-xs text-stone-400">Nessuna foto (blocco solo testo)</span>
                                )}
                                <button
                                    data-testid={`services-block-photo-upload-${i}`}
                                    onClick={() => fileRefs.current[i]?.click()}
                                    disabled={uploadingIdx === i}
                                    className="inline-flex items-center gap-2 border border-stone-900/20 px-4 py-2 text-xs text-stone-600 transition-colors hover:border-[#E75C15] hover:text-stone-900 disabled:opacity-50"
                                >
                                    {uploadingIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                                    {b.photo ? "Cambia foto" : "Aggiungi foto"}
                                </button>
                                <input
                                    ref={(el) => (fileRefs.current[i] = el)}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => uploadPhoto(i, e)}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-4">
                <button
                    data-testid="services-add-block"
                    onClick={() => setBlocks((bs) => [...bs, { title: "", text: "", photo: "" }])}
                    className="inline-flex items-center gap-2 rounded-full border border-stone-900/20 px-6 py-3 text-xs font-medium uppercase tracking-wider text-stone-600 transition-colors hover:border-[#E75C15] hover:text-stone-900"
                >
                    <Plus className="h-4 w-4" />
                    Aggiungi Blocco
                </button>
                <button
                    data-testid="services-page-save"
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-8 py-3 text-xs font-medium uppercase tracking-wider text-stone-100 transition-colors duration-300 hover:bg-[#B8430D] disabled:opacity-60"
                >
                    {saving ? (
                        <>
                            Salvo… <Loader2 className="h-4 w-4 animate-spin" />
                        </>
                    ) : (
                        "Salva la Pagina"
                    )}
                </button>
            </div>
        </div>
    );
};
